import { IDocumentTokenDto } from "@/models/document-token.entity";
import { VuetifyMaterialColorEnum } from "@/lib/enum/vuetify-color.enum";
import { BaseTokenEntity } from "./base-token.entity";
import { $t } from "@/lib/utility/t";
import VueI18n from "@/plugins/I18nPlugin";

function parsePlaceholderToken(token: string): { placeholderKey: string; formatter?: string } {
  const stripped = token.replaceAll("{", "").replaceAll("}", "").trim();
  const formatterIdx = stripped.indexOf("#");
  if (formatterIdx >= 0) {
    const formatter = stripped.slice(0, formatterIdx).trim();
    const placeholderKey = stripped.slice(formatterIdx + 1).trim();
    return {
      formatter: formatter || undefined,
      placeholderKey: placeholderKey || stripped
    };
  }

  return { placeholderKey: stripped };
}

export class PlaceholderTokenEntity extends BaseTokenEntity {
  static readonly SENTINEL = "{{placeholder}}";

  static isPlaceholder(token: string): boolean {
    return token.startsWith("{{") && token !== "{{sign.qr}}" && token !== "{{sign.img}}" && token !== "{{signature}}";
  }

  placeholderKey: string;
  formatter?: string;
  fontSize: number;

  constructor(
    data: Partial<PlaceholderTokenEntity> & {
      x: number;
      y: number;
      w?: number;
      h?: number;
      token?: string;
    }
  ) {
    super({ ...data, value: data.value });
    const parsed = parsePlaceholderToken(data.token ?? PlaceholderTokenEntity.SENTINEL);
    this.placeholderKey = data.placeholderKey ?? parsed.placeholderKey;
    this.formatter = data.formatter ?? parsed.formatter;
    this.fontSize = data.fontSize ?? 12;
  }

  get colorHex() {
    return VuetifyMaterialColorEnum.GREY;
  }

  get icon() {
    return "mdi-variable";
  }

  get color() {
    return "grey";
  }

  get labelKey() {
    return "sign.fieldType.placeholder";
  }

  get tokenString() {
    if (!this.placeholderKey) {
      return PlaceholderTokenEntity.SENTINEL;
    }
    if (this.formatter) {
      return `{{${this.formatter}# ${this.placeholderKey}}}`;
    }
    return `{{${this.placeholderKey}}}`;
  }

  get defaultSize() {
    return { w: 0.3, h: 0.02 };
  }

  get fixedSize() {
    return false;
  }

  validateConstraints(pageWidthMm?: number, pageHeightMm?: number) {
    if (!pageHeightMm) return;
    this.y = Math.max(0, Math.min(pageHeightMm - this.h, this.y));
  }

  toDto(widthPx: number, heightPx: number): IDocumentTokenDto {
    return this.buildDto(widthPx, heightPx, this.fontSize);
  }

  /**
   * Resolves a human-readable label for a placeholder token string using i18n.
   *
   * Simple token:
   *   "{{report.numberplate}}" → "Schadenmeldung: Kennzeichen"
   *
   * Formatted token (Handlebars block helper syntax):
   *   "{{#ddmmyyyy report.registration.firstregistration /}}" → "Schadenmeldung: Datum dieser Zulassung TT.MM.JJJJ"
   *
   * Returns "" for unknown paths, the sentinel placeholder, or empty input.
   */
  static titleForTokenString(token: string): string {
    const stripped = token.replaceAll("{", "").replaceAll("}", "").trim();
    const sentinel = PlaceholderTokenEntity.SENTINEL.replaceAll("{", "").replaceAll("}", "").trim();
    if (!stripped || stripped === sentinel) return "";
    if (stripped.includes("#") && stripped.includes(" ") && stripped.includes("/")) {
      const bits = stripped.split(" ");
      const formatter = bits[0].replaceAll("#", "");
      const path = bits[1] ?? "";
      const pathKey = `components.template.editor.placeholder.path.${path}`;
      const formatterKey = `components.template.editor.placeholder.formatters.${formatter}`;
      if (VueI18n.te(pathKey) && VueI18n.te(formatterKey)) return `${$t(pathKey)} ${$t(formatterKey)}`.trim();
      return "";
    }
    const pathKey = `components.template.editor.placeholder.path.${stripped}`;
    return VueI18n.te(pathKey) ? $t(pathKey) : "";
  }

  static fromTokenString(
    tokenStr: string,
    data: Partial<PlaceholderTokenEntity> & {
      x: number;
      y: number;
      w?: number;
      h?: number;
    }
  ): PlaceholderTokenEntity {
    return new PlaceholderTokenEntity({
      ...data,
      token: tokenStr
    });
  }
}
